import { useState, useEffect } from 'react'

import { Button } from '@elsapiens/ui'

import Modal from './modal'

export interface CurrencyConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  currencyCode: string
  entityLabel: string
  isLoading?: boolean
}

interface FooterProps {
  isLoading: boolean
  isMatch: boolean
  onClose: () => void
  onConfirm: () => void
}

function DialogFooter({ isLoading, isMatch, onClose, onConfirm }: FooterProps): JSX.Element {
  return (
    <>
      <Button disabled={isLoading} size="sm" variant="ghost" onClick={onClose}>
        Go Back
      </Button>
      <Button
        disabled={!isMatch}
        isLoading={isLoading}
        loadingText="Creating..."
        size="sm"
        variant="primary"
        onClick={onConfirm}
      >
        Confirm & Create
      </Button>
    </>
  )
}

interface DialogBodyProps {
  currencyCode: string
  entityLabel: string
  input: string
  setInput: (v: string) => void
  isMatch: boolean
  isLoading: boolean
  onConfirm: () => void
}

function DialogBody({ currencyCode, entityLabel, input, setInput, isMatch, isLoading, onConfirm }: DialogBodyProps): JSX.Element {
  return (
    <div className="el-gap-field">
      <p className="text-sm text-foreground-secondary">
        The currency for this {entityLabel} is set to <strong className="text-foreground">{currencyCode}</strong>.
        Currency cannot be changed after creation.
      </p>
      <p className="text-sm text-foreground-secondary">
        Please type <strong className="text-foreground">{currencyCode}</strong> below to confirm.
      </p>
      {/* eslint-disable jsx-a11y/no-autofocus -- intentional UX: confirmation input requires immediate focus */}
      <input
        autoFocus
        className="el-input w-full"
        placeholder={currencyCode}
        type="text"
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && isMatch && !isLoading) { onConfirm() }
        }}
      />
      {/* eslint-enable jsx-a11y/no-autofocus */}
    </div>
  )
}

export function CurrencyConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  currencyCode,
  entityLabel,
  isLoading = false,
}: CurrencyConfirmDialogProps): JSX.Element {
  const [input, setInput] = useState('')

  useEffect(() => {
    if (isOpen) { setInput('') }
  }, [isOpen])

  const isMatch = input.trim().toUpperCase() === currencyCode.toUpperCase()

  return (
    <Modal
      closeOnEscape={!isLoading}
      closeOnOutsideClick={!isLoading}
      footer={<DialogFooter isLoading={isLoading} isMatch={isMatch} onClose={onClose} onConfirm={onConfirm} />}
      isOpen={isOpen}
      size="sm"
      title="Confirm Currency"
      onClose={onClose}
    >
      <DialogBody
        currencyCode={currencyCode}
        entityLabel={entityLabel}
        input={input}
        isLoading={isLoading}
        isMatch={isMatch}
        setInput={setInput}
        onConfirm={onConfirm}
      />
    </Modal>
  )
}
